import when from 'when';
import rest from 'rest';
import has from 'lodash/has';
import includes from 'lodash/includes';
import reduce from 'lodash/reduce';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import { normalize } from 'normalizr';

import * as requestMethodTypes from '../../constants/requestMethods';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import JSONUtils from '../../lib/JSONUtils';
import { setRequestProgress } from '../../actions';

const versionInfo = {
  version: process.env.REACT_APP_VERSION || '0.0.0',
  versionHash: process.env.REACT_APP_VERSION_HASH || 'local',
  environment: process.env.NODE_ENV || 'production',
};

const APP_CLIENT_INFO = `nexla-ui/${versionInfo.version} (Web; ${versionInfo.versionHash}) ${navigator.userAgent}`;

export const callPrivateAPI = (
  endpoint,
  requestMethod = requestMethodTypes.GET,
  aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key,
  payload = null,
  schema = null,
  accessToken = null,
  org = null,
  isMultipart = false,
  requestType = null,
  store = null,
  extraHeaders = null,
) => {
  function onResponse(response) {
    if (response.status.code >= 400) {
      // console.log('rsp error api call');
      // console.log(response);
      return Promise.reject(response);
    } else if (
      has(response, 'headers["Content-Type"]') &&
      !includes(response.headers['Content-Type'], 'application/json') &&
      !includes(response.headers['Content-Type'], 'application/vnd.nexla.api.v1+json')
    ) {
      console.log('did not get json response!');
      return Promise.reject(new Error('Sorry, request could not be completed. Please try again.'));
    }
    //extract data from response
    let responseJSON =
      has(response, 'entity') && response.entity && response.entity !== ''
        ? JSONUtils.parseJson(response.entity)
        : null;
    let rsp = null;

    if (responseJSON && schema) {
      rsp = normalize(responseJSON, schema);
    } else if (responseJSON) {
      rsp = { entities: null, result: responseJSON };
    } else {
      rsp = responseJSON;
    }

    let responseHeaders = has(response, 'headers') ? response.headers : {};
    return { rsp: rsp, rspHeaders: responseHeaders };
  }

  let requestHeaders = {
    //Accept: 'application/vnd.nexla.api.v1+json'
    // 'cache-control': 'no-store'
    'X-Nexla-Client-Info': APP_CLIENT_INFO,
    'X-Nexla-App-Version': versionInfo.version,
    'X-Nexla-App-Hash': versionInfo.versionHash,
  };

  if (!isMultipart) {
    requestHeaders['Content-Type'] = 'application/json';
    requestHeaders['Accept'] = 'application/vnd.nexla.api.v1+json';
  } else {
    requestHeaders['Accept'] = 'application/json';
  }

  if (extraHeaders) {
    Object.assign(requestHeaders, extraHeaders);
  }

  //TODO: check if token has expired already?
  requestHeaders['Authorization'] = `Bearer ${accessToken}`;

  let requestPayload = payload;
  if (aclQueryMode === ACLQueryModeTypes.MODE_COLLABORATOR.key) {
    requestPayload = !isEmpty(payload)
      ? { access_role: 'collaborator', ...payload }
      : { access_role: 'collaborator' };
  }

  if (isMultipart) {
    const requestEntity = new FormData();

    forEach(requestPayload, (value, key) => {
      requestEntity.append(key, value);
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) =>
        store.dispatch(setRequestProgress(e.loaded, e.total, requestType)),
      );
      xhr.addEventListener('error', () => reject('Upload failed!'));
      xhr.addEventListener('load', (e) =>
        resolve(
          onResponse({
            status: { code: xhr.status },
            entity: xhr.response,
          }),
        ),
      );
      xhr.open(requestMethod, endpoint);
      forEach(requestHeaders, (value, key) => xhr.setRequestHeader(key, value));
      xhr.send(requestEntity);
    });
  }

  const requestEntity = requestPayload ? JSON.stringify(requestPayload) : null;
  let getPayload = null;
  if (requestMethod === requestMethodTypes.GET && requestPayload) {
    getPayload = reduce(
      requestPayload,
      (result, val, key) => {
        return `${result}${key}=${val}&`;
      },
      '?',
    );
    endpoint += getPayload.slice(0, -1);
  }

  return when(
    rest({
      path: endpoint,
      method: requestMethod,
      crossOrigin: true,
      entity: requestEntity,
      headers: requestHeaders,
    }),
  ).then(onResponse, (err) => {
    // console.log('rsp err');
    return Promise.reject(err);
  });
};
